import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    // A late-loading font can make the source strip narrower after it has
    // already been measured, leaving too few copies to fill the viewport.
    await document.fonts?.ready;
    if (!this.isConnected) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies - 1);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.#handleResize.cancel();
  }

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const firstItem = marqueeItems[0];

    if (!this.isConnected || !firstItem) {
      return { numberOfCopies: 1, isHorizontalResize: true };
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (!this.isConnected) {
      return { numberOfCopies: 1, isHorizontalResize: false };
    }

    const marqueeWidth = this.getBoundingClientRect().width;
    const marqueeItemsWidth = firstItem.getBoundingClientRect().width;
    const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
    this.#marqueeWidth = marqueeWidth;

    return {
      numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeItemsWidth)),
      isHorizontalResize,
    };
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize || !this.isConnected || !this.refs.content || !this.refs.wrapper) return;

    // Only count copies in the original content. The wrapper also contains a
    // duplicate of the entire content strip for the seamless loop.
    const currentNumberOfCopies = this.refs.content.children.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    if (!this.isConnected || !this.refs.wrapper) return;

    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    if (!this.isConnected || !this.refs.content || !this.refs.wrapper) return;

    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    clone.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopiesToAdd
   */
  #addRepeatedItems(numberOfCopiesToAdd) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopiesToAdd; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
